"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createToolbarPositioner = void 0;
const extensions_1 = require("remirror/extensions");
const remirror_1 = require("remirror");
const getCursorRect_1 = require("./getCursorRect");
/* istanbul ignore next */
const createToolbarPositioner = ({ types }) => {
    // Inspired by "createMarkPositioner".
    // See: https://github.com/remirror/remirror/blob/107cba/packages/remirror__extension-positioner/src/core-positioners.ts#L267
    return extensions_1.Positioner.create({
        hasChanged: extensions_1.hasStateChanged,
        getActive: (props) => {
            const { state, view } = props;
            try {
                const selection = (0, remirror_1.getTextSelection)(state.selection, state.doc);
                const cursor = { from: view.coordsAtPos(selection.from), to: view.coordsAtPos(selection.to) };
                const data = {
                    isSelectionInView: (0, extensions_1.isPositionVisible)((0, getCursorRect_1.getCursorRect)(cursor.from), view.dom) ||
                        (0, extensions_1.isPositionVisible)((0, getCursorRect_1.getCursorRect)(cursor.to), view.dom),
                    cursor: cursor,
                    visible: false,
                    marks: {},
                };
                data.visible = !selection.empty && data.isSelectionInView;
                types.forEach((type) => {
                    const markRange = (0, remirror_1.getMarkRange)(selection.$from, type, selection.$to);
                    if (!markRange) {
                        data.marks[type] = { isActive: false, isExclusivelyActive: false };
                        return;
                    }
                    // exclusively active =
                    //   the entire selection has the mark applied.
                    // active =
                    //   at least part of the selection has the mark applied. "getMarkRanges" will return an empty array
                    //   if this isn't the case. if there is no selection the cursor must be within the bounds of the mark,
                    //   not on the edges.
                    const isExclusivelyActive = selection.empty
                        ? selection.from > markRange.from && selection.to < markRange.to
                        : selection.from >= markRange.from && selection.to <= markRange.to;
                    const isActive = selection.empty ? selection.from > markRange.from && selection.to < markRange.to : true;
                    // the toolbar will be visible if there is either a selection, or we are within the bounds of a mark
                    // we have formatting tools for.
                    data.visible = data.visible || isExclusivelyActive;
                    data.marks[type] = { isExclusivelyActive, isActive };
                });
                return data.visible ? [data] : extensions_1.Positioner.EMPTY;
            }
            catch {
                return extensions_1.Positioner.EMPTY;
            }
        },
        getPosition: (props) => {
            const { element, data, view } = props;
            const { cursor, visible } = data;
            const { from, to } = cursor;
            const parent = element.offsetParent ?? view.dom;
            const parentRect = parent.getBoundingClientRect();
            const height = Math.abs(to.bottom - from.top);
            // Hack to get JSDOM to work, positioning doesn't work great here.
            if (isNaN(parentRect.top)) {
                return {
                    rect: new DOMRect(0, 0, 0, 0),
                    y: 0,
                    x: 0,
                    height: 0,
                    width: 0,
                    visible: false,
                };
            }
            // True when the selection spans multiple lines.
            const spansMultipleLines = height > from.bottom - from.top;
            // The position furthest to the left.
            const leftmost = Math.min(from.left, to.left);
            // The position nearest the top.
            const topmost = Math.min(from.top, to.top);
            const bottommost = Math.max(from.bottom, to.bottom);
            const left = parent.scrollLeft + (spansMultipleLines ? to.left - parentRect.left : leftmost - parentRect.left);
            const top = parent.scrollTop + topmost - parentRect.top;
            const width = spansMultipleLines ? 1 : Math.abs(from.left - to.right);
            const rect = new DOMRect(spansMultipleLines ? to.left : leftmost, topmost, width, height);
            // Get header toolbar by class inside the current element/editor
            const headerToolbar = element.parentElement?.getElementsByClassName('header-toolbar')?.[0];
            const headerBarRects = headerToolbar?.getBoundingClientRect();
            const headerBarHeight = headerBarRects?.height ?? 0;
            const headerBarBottom = (headerBarRects?.top ?? 0) + headerBarHeight;
            // If floating toolbar will overlap fixed toolbar, set position to under selection
            const isOverlapping = headerBarBottom + headerBarHeight > topmost;
            // This is a good bottom position instead of it being on top of the toolbar when there is an overlap
            const bottomPosition = parent.scrollTop + bottommost - parentRect.top + 62;
            const y = isOverlapping ? bottomPosition : top;
            return { rect, y, x: left, height, width, visible };
        },
    });
};
exports.createToolbarPositioner = createToolbarPositioner;
